import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '@modernhealth/vital-components';
import { filterNullish } from '@modernhealth/filter-nullish';
import { useAnalytics } from '@modernhealth/web-analytics';

import type { CoachingVisit } from 'src/modules/videoSessions/rematchModels/videoSessions';
import { addToCalendar } from 'src/modules/videoSessions/helpers/addToCalendar';
import calendarIcon from 'src/modules/videoSessions/assets/calendar-icon.svg';
import googleCalendarIcon from 'src/modules/videoSessions/assets/calendar-icon--google-calendar.svg';

export enum Calendar {
    GOOGLE_CALENDAR = 'google-calendar',
    ICS = 'ics',
}

export function getCalendarsForSession(session: CoachingVisit | undefined): Calendar[] {
    return filterNullish([
        session?.googleCalendarEventUrl ? Calendar.GOOGLE_CALENDAR : undefined,
        Calendar.ICS,
    ]);
}

export function openCalendarForSession(session: CoachingVisit, calendar: Calendar): void {
    if (calendar === Calendar.ICS) {
        addToCalendar(session.uuid);
    } else if (session.googleCalendarEventUrl && calendar === Calendar.GOOGLE_CALENDAR) {
        window.open(session.googleCalendarEventUrl, '_blank');
    }
}

interface CalendarProductConfig {
    iconImageSrc: string;
    productName: string; // left untranslated
}

interface CalendarGenericConfig {
    iconImageSrc: string;
    labelI18nKey: string;
    labelSuffix?: string; // left untranslated
}

const CalendarConfig: Record<Calendar, CalendarProductConfig | CalendarGenericConfig> = {
    [Calendar.GOOGLE_CALENDAR]: {
        iconImageSrc: googleCalendarIcon,
        productName: 'Google Calendar',
    },
    [Calendar.ICS]: {
        iconImageSrc: calendarIcon,
        labelI18nKey: 'videoSessions:session_confirmation_modal.add_to_calendar',
        labelSuffix: ' (.ics)',
    },
};

const Image = styled.img({
    width: 18,
    marginRight: 12,
    transform: 'translateY(-1px)',
});

interface Props {
    visible: boolean;
    onAddToCalendar: (calendar: Calendar) => void;
    onCancel: () => void;
    calendars: Calendar[];
}

export const AddToCalendarModal: React.FC<Props> = ({
    visible,
    calendars,
    onAddToCalendar,
    onCancel,
}) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();

    const handleCancel = () => {
        analytics.trackClick('Add to Calendar Modal: Closed');
        onCancel();
    };

    return (
        <Modal
            isVisible={visible}
            title={t('videoSessions:session_confirmation_modal.add_to_calendar_modal.title')}
            onCloseButtonClick={handleCancel}
            maxWidth={450}
        >
            {calendars.map((calendar, i) => {
                const config = CalendarConfig[calendar];
                if (!config) {
                    return null;
                }

                const buttonLabel =
                    'productName' in config
                        ? t('videoSessions:session_confirmation_modal.add_to_calendar_product', {
                              calendar: config.productName,
                          })
                        : t(config.labelI18nKey) + (config.labelSuffix ?? '');

                return (
                    <Button
                        key={calendar}
                        variant="secondary"
                        css={{
                            marginTop: i === 0 ? 20 : 13,
                        }}
                        rounded
                        fluid
                        onClick={() => {
                            analytics.trackClick(`Add to Calendar Modal: Opened ${calendar}`, {
                                button_copy: buttonLabel,
                            });
                            onAddToCalendar(calendar);
                        }}
                    >
                        <Image src={config.iconImageSrc} alt="" />
                        {buttonLabel}
                    </Button>
                );
            })}
        </Modal>
    );
};
